import { getDisplayPrice, type DisplayPrice } from '@/features/menu/utils/pricing';
import { loc } from '@/utils/localized';
import {
  CategoryScheduleSchema,
  menuChangeTarget,
  type Category,
  type CategorySchedule,
  type MenuChange,
  type MenuChangeKind,
  type MenuOverrideChannel,
  type MenuTree,
  type OptionChoice,
  type OptionRules,
  type Product,
  type ProductOption,
} from '@restaurant/api-contracts';
import { priceGroupOf } from '@restaurant/types';

export interface Draft<T> {
  before: T;
  after: T;
}

export type DraftStatus = 'new' | 'removed' | 'moved' | null;

export const canOpenPricing = (view: ProductView): boolean => {
  if (view.status === 'new' || view.status === 'removed') return false;
  if (priceGroupOf(view.product) !== null) return false;
  const { before, after } = view.price;
  const priceChanged = !!before && !!after && before.amount !== after.amount;
  return !view.basePrice && view.channels.length === 0 && !priceChanged;
};

export interface ChannelDraft {
  channel: MenuOverrideChannel;
  before: number | null;
  after: number | null;
  base: number;
}

export type SubLine = { key: string; label: string } & (
  | {
      type: 'status';
      status: 'new' | 'removed';
      price: number | null;
      sign: '' | '+';
      rules: OptionRules | null;
    }
  | { type: 'text'; before: string; after: string }
  | { type: 'money'; before: number; after: number; sign: '' | '+' }
  | { type: 'channel'; sign: '' | '+'; draft: ChannelDraft }
  | { type: 'rules'; before: OptionRules; after: OptionRules }
  | { type: 'flag'; flag: ChoiceFlag; after: boolean }
  | { type: 'order'; before: string[]; after: string[] }
);

export type ChoiceFlag = 'inStock' | 'active' | 'default';

export interface ChoiceView {
  choice: OptionChoice;
  status: DraftStatus;
  changed: boolean;
  label: string;
  name?: Draft<string>;
  price?: Draft<number>;
  channels: ChannelDraft[];
  flags: { flag: ChoiceFlag; after: boolean }[];
}

export interface OptionView {
  option: ProductOption;
  status: DraftStatus;
  changed: boolean;
  label: string;
  sign: '' | '+';
  rulesAfter: OptionRules;
  name?: Draft<string>;
  rules?: Draft<OptionRules>;
  choiceOrder?: Draft<string[]>;
  choices: ChoiceView[];
}

export interface ProductView {
  product: Product;
  status: DraftStatus;
  changed: boolean;
  price: Draft<DisplayPrice | null>;
  basePrice?: Draft<number>;
  name?: Draft<string>;
  description?: Draft<string>;
  inStock?: Draft<boolean>;
  active?: Draft<boolean>;
  codes?: Draft<string[]>;
  labels?: Draft<string[]>;
  hours?: Draft<CategorySchedule | null>;
  channels: ChannelDraft[];
  options: OptionView[];
  subs: SubLine[];
}

export interface SectionView {
  category: Category;
  status: DraftStatus;
  changed: boolean;
  name?: Draft<string>;
  active?: Draft<boolean>;
  hours?: Draft<CategorySchedule | null>;
  products: ProductView[];
}

export interface MenuDraft {
  sections: SectionView[];
  targets: string[];
}

type ChangeOf<K extends MenuChangeKind> = Extract<MenuChange, { kind: K }>;

const pick = <K extends MenuChangeKind>(changes: MenuChange[], kind: K): ChangeOf<K>[] =>
  changes.filter((change): change is ChangeOf<K> => change.kind === kind);

const bySortOrder = <T extends { sortOrder: number; id: string }>(items: T[]): T[] =>
  items.slice().sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));

export function scheduleOf(value: unknown): CategorySchedule | null {
  const parsed = CategoryScheduleSchema.safeParse(value);
  return parsed.success && parsed.data.windows.length > 0 ? parsed.data : null;
}

export function movedItems(before: string[], after: string[]): Set<string> {
  const position = new Map(before.map((id, i) => [id, i]));
  const kept = after.filter((id) => position.has(id));
  const tails: number[] = [];
  const tailIndex: number[] = [];
  const parent = new Array<number>(kept.length).fill(-1);
  kept.forEach((id, i) => {
    const value = position.get(id) ?? 0;
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if ((tails[mid] ?? 0) < value) low = mid + 1;
      else high = mid;
    }
    tails[low] = value;
    tailIndex[low] = i;
    parent[i] = low > 0 ? (tailIndex[low - 1] ?? -1) : -1;
  });
  const stay = new Set<string>();
  let cursor = tailIndex[tails.length - 1] ?? -1;
  while (cursor >= 0) {
    const id = kept[cursor];
    if (id !== undefined) stay.add(id);
    cursor = parent[cursor] ?? -1;
  }
  return new Set(kept.filter((id) => !stay.has(id)));
}

function newCategory(change: ChangeOf<'categoryCreate'>, menuId: string): Category {
  return {
    id: change.categoryId,
    menuId,
    name: { en: change.name },
    description: null,
    imageUrl: null,
    schedule: null,
    sortOrder: Number.MAX_SAFE_INTEGER,
    isActive: true,
    hiddenChannels: [],
  };
}

function newProduct(change: ChangeOf<'productCreate'>): Product {
  return {
    id: change.productId,
    categoryId: change.categoryId,
    code: null,
    name: { en: change.name },
    description: change.description ? { en: change.description } : null,
    imageUrl: null,
    schedule: null,
    price: change.price,
    allergens: [],
    allergenCodes: change.allergenCodes,
    labels: change.labels,
    sortOrder: Number.MAX_SAFE_INTEGER,
    isActive: true,
    inStock: true,
    options: [],
  };
}

function newOption(change: ChangeOf<'optionCreate'>): ProductOption {
  return {
    id: change.optionId,
    productId: change.productId,
    name: { en: change.name },
    ...change.rules,
    sortOrder: Number.MAX_SAFE_INTEGER,
    choices: [],
  };
}

function newChoice(change: ChangeOf<'optionChoiceCreate'>): OptionChoice {
  return {
    id: change.optionChoiceId,
    optionId: change.optionId,
    name: { en: change.name },
    price: change.price,
    allergens: [],
    allergenCodes: [],
    isDefault: change.isDefault,
    isActive: true,
    inStock: true,
    sortOrder: Number.MAX_SAFE_INTEGER,
  };
}

const rulesOf = (option: ProductOption): OptionRules => ({
  isRequired: option.isRequired,
  isMultiple: option.isMultiple,
  minSelect: option.minSelect,
  maxSelect: option.maxSelect,
});

function changeContext(changes: MenuChange[]): Omit<ProductContext, 'movedIn'> {
  const byTarget = new Map<string, MenuChange[]>();
  for (const change of changes) {
    const id = menuChangeTarget(change);
    byTarget.set(id, [...(byTarget.get(id) ?? []), change]);
  }
  const all = <K extends MenuChangeKind>(id: string, kind: K): ChangeOf<K>[] =>
    pick(byTarget.get(id) ?? [], kind);
  const of = <K extends MenuChangeKind>(id: string, kind: K): ChangeOf<K> | undefined =>
    all(id, kind)[0];
  const removed = new Set(
    changes.flatMap((c) =>
      (c.kind === 'categoryRemoved' ||
        c.kind === 'productRemoved' ||
        c.kind === 'optionRemoved' ||
        c.kind === 'optionChoiceRemoved') &&
      c.after
        ? [menuChangeTarget(c)]
        : [],
    ),
  );
  const newOptions = new Map(pick(changes, 'optionCreate').map((c) => [c.optionId, newOption(c)]));
  const newChoices = pick(changes, 'optionChoiceCreate');
  const created = new Set([
    ...pick(changes, 'categoryCreate').map((c) => c.categoryId),
    ...pick(changes, 'productCreate').map((c) => c.productId),
    ...newOptions.keys(),
    ...newChoices.map((c) => c.optionChoiceId),
  ]);
  return { of, all, removed, created, newOptions, newChoices };
}

export function buildMenuDraft(tree: MenuTree, changes: MenuChange[]): MenuDraft {
  const context = changeContext(changes);
  const { of, removed, created } = context;
  const categories = new Map<string, Category & { products: Product[] }>();
  const products = new Map<string, Product>();
  for (const category of tree.categories) {
    categories.set(category.id, category);
    for (const product of category.products) products.set(product.id, product);
  }
  for (const change of pick(changes, 'categoryCreate')) {
    categories.set(change.categoryId, { ...newCategory(change, tree.id), products: [] });
  }
  for (const change of pick(changes, 'productCreate')) {
    products.set(change.productId, newProduct(change));
  }

  const sectionBefore = bySortOrder(tree.categories).map((c) => c.id);
  const categoryOrder = pick(changes, 'categoryOrder')[0]?.after ?? [
    ...sectionBefore,
    ...pick(changes, 'categoryCreate').map((c) => c.categoryId),
  ];
  const movedSections = movedItems(sectionBefore, categoryOrder);
  const productOrders = new Map(pick(changes, 'productOrder').map((c) => [c.categoryId, c.after]));
  const placedAnywhere = new Set([...productOrders.values()].flat());
  const targets: string[] = [];

  const sections = categoryOrder.flatMap((categoryId): SectionView[] => {
    const category = categories.get(categoryId);
    if (!category) return [];
    const liveBefore = bySortOrder(category.products).map((p) => p.id);
    const order = productOrders.get(categoryId) ?? [
      ...liveBefore.filter((id) => !placedAnywhere.has(id)),
      ...pick(changes, 'productCreate')
        .filter((c) => c.categoryId === categoryId && !placedAnywhere.has(c.productId))
        .map((c) => c.productId),
    ];
    const moved = movedItems(liveBefore, order);
    const name = of(categoryId, 'categoryName');
    const active = of(categoryId, 'categoryActive');
    const schedule = of(categoryId, 'categorySchedule');
    const status: DraftStatus = created.has(categoryId)
      ? 'new'
      : removed.has(categoryId)
        ? 'removed'
        : movedSections.has(categoryId)
          ? 'moved'
          : null;
    const changed = status !== null || !!name || !!active || !!schedule;
    if (changed) targets.push(categoryId);
    const views = order.flatMap((productId): ProductView[] => {
      const product = products.get(productId);
      if (!product) return [];
      const view = productView(product, {
        ...context,
        movedIn: moved.has(productId) || !liveBefore.includes(productId),
      });
      if (view.changed) targets.push(productId);
      return [view];
    });
    return [
      {
        category,
        status,
        changed,
        ...(name ? { name } : {}),
        ...(active ? { active } : {}),
        ...(schedule ? { hours: schedule } : {}),
        products: views,
      },
    ];
  });
  return { sections, targets };
}

export type ProductField =
  | 'name'
  | 'description'
  | 'price'
  | 'active'
  | 'inStock'
  | 'hours'
  | 'codes'
  | 'labels';

export const productFieldTarget = (productId: string, field: ProductField): string =>
  `${productId}:${field}`;

export interface ProductDraft {
  view: ProductView;
  targets: string[];
}

export function buildProductDraft(product: Product, changes: MenuChange[]): ProductDraft {
  const view = productView(product, { ...changeContext(changes), movedIn: false });
  const field = (name: ProductField, on: boolean) =>
    on ? [productFieldTarget(product.id, name)] : [];
  const optionTargets = view.options.flatMap((option) => [
    ...(option.status || option.name || option.rules || option.choiceOrder
      ? [option.option.id]
      : []),
    ...option.choices.flatMap((choice) => (choice.changed ? [choice.choice.id] : [])),
  ]);
  return {
    view,
    targets: [
      ...field('name', !!view.name),
      ...field('description', !!view.description),
      ...field('price', !!view.basePrice || view.channels.length > 0),
      ...optionTargets,
      ...field('active', !!view.active),
      ...field('inStock', !!view.inStock),
      ...field('hours', !!view.hours),
      ...field('codes', !!view.codes),
      ...field('labels', !!view.labels),
    ],
  };
}

interface ProductContext {
  of: <K extends MenuChangeKind>(id: string, kind: K) => ChangeOf<K> | undefined;
  all: <K extends MenuChangeKind>(id: string, kind: K) => ChangeOf<K>[];
  removed: Set<string>;
  created: Set<string>;
  newOptions: Map<string, ProductOption>;
  newChoices: ChangeOf<'optionChoiceCreate'>[];
  movedIn: boolean;
}

function withTrailing(order: string[] | undefined, allIds: string[]): string[] {
  if (!order) return allIds;
  const inOrder = new Set(order);
  return [...order, ...allIds.filter((id) => !inOrder.has(id))];
}

function optionViews(product: Product, context: ProductContext) {
  const { of, removed, created } = context;
  const id = product.id;
  const existingOptions = bySortOrder(product.options);
  const freshOptions = [...context.newOptions.values()].filter((o) => o.productId === id);
  const optionsById = new Map([...existingOptions, ...freshOptions].map((o) => [o.id, o]));
  const optionOrderChange = of(id, 'optionOrder');
  const optionOrder = withTrailing(optionOrderChange?.after, [...optionsById.keys()]);
  const movedOptions = optionOrderChange
    ? movedItems(optionOrderChange.before, optionOrderChange.after)
    : new Set<string>();
  const statusOf = (itemId: string, moved: Set<string>): DraftStatus =>
    created.has(itemId)
      ? 'new'
      : removed.has(itemId)
        ? 'removed'
        : moved.has(itemId)
          ? 'moved'
          : null;

  const options = optionOrder.flatMap((optionId): Omit<OptionView, 'sign'>[] => {
    const option = optionsById.get(optionId);
    if (!option) return [];
    const name = of(optionId, 'optionName');
    const rules = of(optionId, 'optionRules');
    const label = name?.after ?? loc(option.name);
    const freshChoices = context.newChoices.filter((c) => c.optionId === optionId).map(newChoice);
    const choicesById = new Map(
      [...bySortOrder(option.choices), ...freshChoices].map((c) => [c.id, c]),
    );
    const choiceOrderChange = of(optionId, 'optionChoiceOrder');
    const movedChoices = choiceOrderChange
      ? movedItems(choiceOrderChange.before, choiceOrderChange.after)
      : new Set<string>();
    const choices = withTrailing(choiceOrderChange?.after, [...choicesById.keys()]).flatMap(
      (choiceId): ChoiceView[] => {
        const choice = choicesById.get(choiceId);
        if (!choice) return [];
        const choiceName = of(choiceId, 'optionChoiceName');
        const price = of(choiceId, 'optionChoicePrice');
        const channels = (['PICKUP', 'DELIVERY'] as const).flatMap((channel): ChannelDraft[] => {
          const change = channelChangeOf(context, choiceId, channel);
          return change
            ? [
                {
                  channel,
                  before: change.before,
                  after: change.after,
                  base: price?.after ?? choice.price,
                },
              ]
            : [];
        });
        const flags = FLAGS.flatMap((flag) => {
          const change = of(choiceId, FLAG_KIND[flag]);
          return change ? [{ flag, after: change.after }] : [];
        });
        const status = statusOf(choiceId, movedChoices);
        return [
          {
            choice,
            status,
            changed:
              status !== null || !!choiceName || !!price || channels.length + flags.length > 0,
            label: `${label} · ${choiceName?.after ?? loc(choice.name)}`,
            ...(choiceName ? { name: choiceName } : {}),
            ...(price ? { price } : {}),
            channels,
            flags,
          },
        ];
      },
    );
    const status = statusOf(optionId, movedOptions);
    const choiceOrder = choiceOrderChange
      ? {
          before: choiceOrderChange.before.map((c) => loc(choicesById.get(c)?.name ?? null)),
          after: choiceOrderChange.after.map((c) => loc(choicesById.get(c)?.name ?? null)),
        }
      : undefined;
    return [
      {
        option,
        status,
        changed:
          status !== null || !!name || !!rules || !!choiceOrder || choices.some((c) => c.changed),
        label,
        rulesAfter: rules?.after ?? rulesOf(option),
        ...(name ? { name } : {}),
        ...(rules ? { rules } : {}),
        ...(choiceOrder ? { choiceOrder } : {}),
        choices,
      },
    ];
  });
  const order = optionOrderChange
    ? {
        before: optionOrderChange.before.map((o) => loc(optionsById.get(o)?.name ?? null)),
        after: optionOrderChange.after.map((o) => loc(optionsById.get(o)?.name ?? null)),
      }
    : undefined;
  return { options, order };
}

function subLines(
  productId: string,
  options: OptionView[],
  order: Draft<string[]> | undefined,
): SubLine[] {
  const subs: SubLine[] = [];
  if (order) subs.push({ key: `optionOrder:${productId}`, label: '', type: 'order', ...order });
  for (const view of options) {
    const optionId = view.option.id;
    if (view.status === 'new' || view.status === 'removed') {
      subs.push({
        key: `option:${optionId}`,
        label: view.label,
        type: 'status',
        status: view.status,
        price: null,
        sign: view.sign,
        rules: view.status === 'new' ? view.rulesAfter : null,
      });
    }
    if (view.name) {
      subs.push({ key: `optionName:${optionId}`, label: '', type: 'text', ...view.name });
    }
    if (view.rules) {
      subs.push({
        key: `optionRules:${optionId}`,
        label: view.label,
        type: 'rules',
        ...view.rules,
      });
    }
    if (view.choiceOrder) {
      subs.push({
        key: `choiceOrder:${optionId}`,
        label: view.label,
        type: 'order',
        ...view.choiceOrder,
      });
    }
    for (const choice of view.choices) {
      const choiceId = choice.choice.id;
      if (choice.status === 'new' || choice.status === 'removed') {
        subs.push({
          key: `choice:${choiceId}`,
          label: choice.label,
          type: 'status',
          status: choice.status,
          price: choice.status === 'new' ? choice.choice.price : null,
          sign: view.sign,
          rules: null,
        });
      }
      if (choice.name) {
        subs.push({
          key: `choiceName:${choiceId}`,
          label: view.label,
          type: 'text',
          ...choice.name,
        });
      }
      if (choice.price) {
        subs.push({
          key: `choicePrice:${choiceId}`,
          label: choice.label,
          type: 'money',
          ...choice.price,
          sign: view.sign,
        });
      }
      for (const draft of choice.channels) {
        subs.push({
          key: `choiceChannel:${choiceId}:${draft.channel}`,
          label: choice.label,
          type: 'channel',
          sign: view.sign,
          draft,
        });
      }
      for (const { flag, after } of choice.flags) {
        subs.push({
          key: `${FLAG_KIND[flag]}:${choiceId}`,
          label: choice.label,
          type: 'flag',
          flag,
          after,
        });
      }
    }
  }
  return subs;
}

function productView(product: Product, context: ProductContext): ProductView {
  const { of, removed, created } = context;
  const id = product.id;
  const priceGroupId = priceGroupOf(product);
  const basePrice = of(id, 'productPrice');
  const afterBase = priceGroupId ? product.price : (basePrice?.after ?? product.price);
  const drafted = optionViews(product, context);
  const afterOptions = drafted.options
    .filter((view) => view.status !== 'removed')
    .map((view, position): ProductOption => ({
      ...view.option,
      ...view.rulesAfter,
      sortOrder: position,
      choices: view.choices
        .filter((c) => c.status !== 'removed')
        .map((c) => ({ ...c.choice, price: c.price?.after ?? c.choice.price })),
    }));
  const afterGroupId = priceGroupOf({ price: afterBase, options: afterOptions });
  const options = drafted.options.map((view): OptionView => ({
    ...view,
    sign: view.option.id === afterGroupId ? '' : '+',
  }));

  const name = of(id, 'productName');
  const description = of(id, 'productDescription');
  const inStock = of(id, 'productAvailability');
  const active = of(id, 'productActive');
  const codes = of(id, 'productAllergenCodes');
  const labels = of(id, 'productLabels');
  const hours = of(id, 'productSchedule');
  const channels = (['PICKUP', 'DELIVERY'] as const).flatMap((channel): ChannelDraft[] => {
    const change = channelChangeOf(context, id, channel);
    return change
      ? [
          {
            channel,
            before: change.before,
            after: change.after,
            base: basePrice?.after ?? product.price,
          },
        ]
      : [];
  });
  const subs = subLines(id, options, drafted.order);
  const isNew = created.has(id);
  const status: DraftStatus = isNew
    ? 'new'
    : removed.has(id)
      ? 'removed'
      : context.movedIn
        ? 'moved'
        : null;
  const beforePrice = isNew ? null : getDisplayPrice(product);
  const afterPrice = getDisplayPrice({ price: afterBase, options: afterOptions });
  const changed =
    status !== null ||
    subs.length > 0 ||
    channels.length > 0 ||
    [name, description, inStock, active, codes, labels, hours, basePrice].some(Boolean) ||
    (beforePrice !== null && beforePrice.amount !== afterPrice.amount);
  return {
    product,
    status,
    changed,
    price: { before: beforePrice, after: afterPrice },
    ...(basePrice ? { basePrice } : {}),
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    ...(inStock ? { inStock } : {}),
    ...(active ? { active } : {}),
    ...(codes ? { codes } : {}),
    ...(labels ? { labels } : {}),
    ...(hours ? { hours } : {}),
    channels,
    options,
    subs,
  };
}

const FLAG_KIND = {
  inStock: 'optionChoiceAvailability',
  active: 'optionChoiceActive',
  default: 'optionChoiceDefault',
} as const satisfies Record<ChoiceFlag, MenuChangeKind>;

const FLAGS: ChoiceFlag[] = ['inStock', 'active', 'default'];

function channelChangeOf(
  context: ProductContext,
  id: string,
  channel: MenuOverrideChannel,
): { before: number | null; after: number | null } | undefined {
  return (
    context.all(id, 'productChannelPrice').find((c) => c.channel === channel) ??
    context.all(id, 'optionChoiceChannelPrice').find((c) => c.channel === channel)
  );
}

function nameProduct(names: Map<string, string>, product: Product): void {
  names.set(product.id, loc(product.name));
  for (const option of product.options) {
    names.set(option.id, `${loc(product.name)} · ${loc(option.name)}`);
    for (const choice of option.choices) {
      names.set(choice.id, `${loc(product.name)} · ${loc(option.name)} · ${loc(choice.name)}`);
    }
  }
}

function labelsOf(names: Map<string, string>, changes: MenuChange[]): string[] {
  for (const change of changes) {
    if ('name' in change && typeof change.name === 'string') {
      names.set(menuChangeTarget(change), change.name);
    }
  }
  return [...new Set(changes.flatMap((change) => names.get(menuChangeTarget(change)) ?? []))];
}

export function changeLabels(tree: MenuTree, changes: MenuChange[]): string[] {
  const names = new Map<string, string>();
  for (const category of tree.categories) {
    names.set(category.id, loc(category.name));
    for (const product of category.products) nameProduct(names, product);
  }
  return labelsOf(names, changes);
}

export function productChangeLabels(product: Product, changes: MenuChange[]): string[] {
  const names = new Map<string, string>();
  nameProduct(names, product);
  return labelsOf(names, changes);
}
